<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useIncidentStore } from '@/store/incident'
import { useCameraName } from '@/composables/cameraHelpers'
import DateTimePicker from '@/components/DateTimePicker.vue'
import FrameSeek from '@/views/FrameSeek.vue'
import { DateTime } from 'luxon'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'

const props = defineProps({
  showCancelButton: { type: Boolean, default: false },
  newIncidentPayload: { type: Object, default: null },
  incident: { type: Object, default: null },
})
const showDialog = defineModel({ type: Boolean, required: true })
const emit = defineEmits(['cancel', 'saved'])

const incidentStore = useIncidentStore()

const showFrameSeek = ref(false)
const detectedTimestamp = ref(null)
const isAlreadyRemoved = ref(false)
const removedTimestamp = ref(null)
const cameraName = computed(() => {
  if (!props.newIncidentPayload) return ''
  return useCameraName(props.newIncidentPayload.cameraId)
})

const timestampWasProvided = computed(() => {
  return !!props.newIncidentPayload?.detection?.timestamp
})

function cancel() {
  emit('cancel')
}

async function triggerDeadAnimal() {
  const triggerPayload = {
    isCurrentDetection: !isAlreadyRemoved.value,
    cameraId: props.newIncidentPayload.cameraId,
    removal: isAlreadyRemoved.value
      ? {
          timestamp: removedTimestamp.value.toMillis(),
        }
      : undefined,
    detection: {
      timestamp: detectedTimestamp.value.toMillis(),
      x: props.newIncidentPayload.detection.x,
      y: props.newIncidentPayload.detection.y,
      w: props.newIncidentPayload.detection.w,
      h: props.newIncidentPayload.detection.h,
      artifactId: props.newIncidentPayload.detection.artifactId,
    },
  }

  await incidentStore.triggerDeadAnimal(triggerPayload)
  emit('saved')
}

async function markAsRemoved() {
  await incidentStore.markDeadAnimalAsRemoved(props.incident.incidentId, removedTimestamp.value.toMillis())
  emit('saved')
}

async function markAsFalsePositive() {
  await incidentStore.markDeadAnimalAsFalsePositive(props.incident.incidentId, props.incident.firstEventTimestamp)
  emit('saved')
}

async function revertFalsePositive() {
  await incidentStore.markDeadAnimalAsNotFalsePositive(props.incident.incidentId)
  emit('saved')
}

watch(
  () => props.newIncidentPayload,
  after => {
    if (after) {
      detectedTimestamp.value = DateTime.fromMillis(props.newIncidentPayload.detection.timestamp)
    }
  },
  { immediate: true }
)

onMounted(() => {
  if (timestampWasProvided.value) detectedTimestamp.value = DateTime.fromMillis(props.newIncidentPayload.detection.timestamp)
})
</script>

<template>
  <GenericCustomDialog
    v-model="showDialog"
    max-width="800px"
    hide-cancel-button
  >
    <template #headerIcon>
      <v-icon icon="mdi-pencil" />
    </template>
    <template #title>
      {{ incident ? $t('incidents.deadAnimal.update.title') : $t('incidents.deadAnimal.trigger_title', { cameraName }) }}
    </template>
    <template #body>
      <div
        v-if="!incident"
        class="d-flex flex-column align-center ga-2"
      >
        <DateTimePicker
          v-model="detectedTimestamp"
          :label="$t('incidents.deadAnimal.detected_timestamp')"
          :hide-details="timestampWasProvided"
        />

        <v-checkbox
          v-model="isAlreadyRemoved"
          :label="$t('incidents.deadAnimal.is_removed')"
          hide-details
        />

        <DateTimePicker
          v-if="isAlreadyRemoved"
          v-model="removedTimestamp"
          :label="$t('incidents.deadAnimal.removed_timestamp')"
        />

        <v-btn
          v-if="isAlreadyRemoved"
          prepend-icon="mdi-fast-forward"
          variant="outlined"
        >
          {{ $t('incidents.deadAnimal.frame_seek_button') }}

          <GenericCustomDialog
            v-model="showFrameSeek"
            activator="parent"
            max-width="1200px"
          >
            <template #headerIcon>
              <v-icon icon="mdi-fast-forward" />
            </template>
            <template #title>
              {{ incident ? $t('incidents.barnState.update_title') : $t('incidents.barnState.new_title') }}
            </template>
            <template #body>
              <FrameSeek
                :pre-picked-camera="props.newIncidentPayload.cameraId"
                :camera-read-only="true"
                :timestamp-picker-mode="true"
                :use-query-string="false"
                @pick-timestamp="
                  () => {
                    removedTimestamp = $event
                    showFrameSeek = false
                  }
                "
              />
            </template>
          </GenericCustomDialog>
        </v-btn>
      </div>

      <div
        v-if="incident"
        class="d-flex flex-column ga-4"
      >
        <v-layout-card color="blue">
          <v-card-title>
            <v-icon
              icon="mdi-check"
              size="small"
            />
            {{ $t('incidents.deadAnimal.update.removed.title') }}
          </v-card-title>
          <v-card-text class="d-flex flex-column ga-4">
            <v-alert type="info">
              {{ $t('incidents.deadAnimal.update.removed.text') }}
            </v-alert>
            <DateTimePicker v-model="removedTimestamp" />
          </v-card-text>
          <v-card-actions>
            <v-btn
              :block="true"
              variant="tonal"
              @click="markAsRemoved"
            >
              {{ $t('incidents.deadAnimal.update.removed.button') }}
            </v-btn>
          </v-card-actions>
        </v-layout-card>

        <v-layout-card
          v-if="incident?.messagePayload?.falsePositive !== true"
          color="red"
        >
          <v-card-title>
            <v-icon
              icon="mdi-cancel"
              size="small"
            />
            {{ $t('incidents.deadAnimal.update.false_positive.title') }}
          </v-card-title>
          <v-card-text>
            <v-alert type="warning">
              {{ $t('incidents.deadAnimal.update.false_positive.text') }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-btn
              :block="true"
              variant="tonal"
              @click="markAsFalsePositive"
            >
              {{ $t('incidents.deadAnimal.update.false_positive.button') }}
            </v-btn>
          </v-card-actions>
        </v-layout-card>

        <v-layout-card
          v-if="incident?.messagePayload?.falsePositive === true"
          color="red"
        >
          <v-card-title>
            <v-icon
              icon="mdi-undo"
              size="small"
            />
            {{ $t('incidents.deadAnimal.update.revert_false_positive.title') }}
          </v-card-title>
          <v-card-text>
            <v-alert type="warning">
              {{ $t('incidents.deadAnimal.update.revert_false_positive.text') }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-btn
              :block="true"
              variant="tonal"
              @click="revertFalsePositive"
            >
              {{ $t('incidents.deadAnimal.update.revert_false_positive.button') }}
            </v-btn>
          </v-card-actions>
        </v-layout-card>

        <v-btn
          v-if="showCancelButton"
          prepend-icon="mdi-close"
          color="error"
          variant="outlined"
          @click="cancel"
        >
          {{ $t('general_interface.buttons.cancel') }}
        </v-btn>
      </div>
    </template>
    <template
      v-if="!incident"
      #actions
    >
      <v-btn
        v-if="showCancelButton"
        prepend-icon="mdi-close"
        color="error"
        variant="outlined"
        @click="cancel"
      >
        {{ $t('general_interface.buttons.cancel') }}
      </v-btn>
      <v-btn
        prepend-icon="mdi-check"
        variant="elevated"
        color="primary"
        @click="triggerDeadAnimal"
      >
        {{ $t('general_interface.buttons.confirm') }}
      </v-btn>
    </template>
  </GenericCustomDialog>
</template>

<style scoped></style>
